/*ABOUTME: Composable for managing policy acceptance throughout the application*/
/*ABOUTME: Handles showing policy dialogs and tracking acceptance state*/
import { useDialog } from "primevue/usedialog";
import { ref } from "vue";

import PolicyAcceptance from "@/components/PolicyAcceptance.vue";
import config from "@/config";
import { useAuthenticationStore } from "@/stores/authentication-store";

const showPolicyDialog = ref(false);
const policyData = ref<{
    isNewUser: boolean;
    versions: { privacy: string; terms: string };
}>({
    isNewUser: false,
    versions: { privacy: "1.0", terms: "1.0" },
});

export const usePolicyAcceptance = () => {
    const authStore = useAuthenticationStore();
    const dialog = useDialog();

    /**
     * Check if policy acceptance is required and show dialog if needed
     */
    const checkAndShowPolicyDialog = async (): Promise<boolean> => {
        try {
            const { required, isNewUser } =
                await authStore.checkPolicyAcceptanceRequired();

            if (required) {
                policyData.value = {
                    isNewUser,
                    versions: config.policyVersions,
                };

                return new Promise((resolve) => {
                    dialog.open(PolicyAcceptance, {
                        props: {
                            modal: true,
                            header: "Terms and Privacy",
                            style: { width: "500px" },
                            closable: false,
                            dismissableMask: false,
                        },
                        data: {
                            isNewUser,
                            policyVersions: config.policyVersions,
                        },
                        onClose: async (options) => {
                            console.log(
                                "Policy dialog closed with options:",
                                options
                            );
                            if (options?.data?.accepted) {
                                console.log(
                                    "User accepted policies, recording..."
                                );
                                try {
                                    await authStore.recordPolicyAcceptance(
                                        options.data.acceptPrivacyPolicy,
                                        options.data.acceptTermsConditions,
                                        config.policyVersions
                                    );
                                    console.log(
                                        "Policy acceptance recorded successfully"
                                    );
                                    resolve(true);
                                } catch (error) {
                                    console.error(
                                        "Failed to record policy acceptance:",
                                        error
                                    );
                                    resolve(false);
                                }
                            } else {
                                console.log("User did not accept policies");
                                resolve(false);
                            }
                        },
                    });
                });
            }

            return true; // No acceptance required
        } catch (error) {
            console.error("Error checking policy acceptance:", error);
            return false;
        }
    };

    /**
     * Force show policy dialog (for testing or manual triggers)
     */
    const showPolicyAcceptanceDialog = async (
        isNewUser: boolean = false
    ): Promise<boolean> => {
        policyData.value = {
            isNewUser,
            versions: config.policyVersions,
        };

        return new Promise((resolve) => {
            dialog.open(PolicyAcceptance, {
                props: {
                    modal: true,
                    header: "Terms and Privacy",
                    style: { width: "500px" },
                    closable: false,
                    dismissableMask: false,
                },
                data: {
                    isNewUser,
                    policyVersions: config.policyVersions,
                },
                onClose: async (options) => {
                    if (options?.data?.accepted) {
                        try {
                            await authStore.recordPolicyAcceptance(
                                options.data.acceptPrivacyPolicy,
                                options.data.acceptTermsConditions,
                                config.policyVersions
                            );
                            resolve(true);
                        } catch (error) {
                            console.error(
                                "Failed to record policy acceptance:",
                                error
                            );
                            resolve(false);
                        }
                    } else {
                        resolve(false);
                    }
                },
            });
        });
    };

    return {
        checkAndShowPolicyDialog,
        showPolicyAcceptanceDialog,
        showPolicyDialog: showPolicyDialog.value,
        policyData: policyData.value,
    };
};
